import React, { useCallback, useEffect, useRef, useState } from "react";
const options = [
  ["DOCUMENTS", "Documentos"],
  ["FINANCIAL", "Financeiro"],
  ["N8N", "Automação n8n"],
];
export function validPurposes(data) {
  return (
    data &&
    Number.isSafeInteger(data.revision) &&
    data.revision >= 0 &&
    data.sendingEnabled === false &&
    Array.isArray(data.purposes) &&
    data.purposes.length <= 3 &&
    new Set(data.purposes).size === data.purposes.length &&
    data.purposes.every((p) => options.some(([key]) => key === p)) &&
    data.principals &&
    ["DOCUMENTS", "FINANCIAL"].every(
      (p) =>
        data.principals[p] === null ||
        (typeof data.principals[p] === "string" && /^[a-z][a-z0-9-]{0,39}$/.test(data.principals[p]))
    )
  );
}
export default function ConnectionPurposes({
  connection,
  request,
  owner,
  disabled,
  refreshKey = 0,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const epoch = useRef(0);
  useEffect(
    () => () => {
      epoch.current += 1;
    },
    [owner, connection.id]
  );
  const load = useCallback(
    async (show = true) => {
      const token = ++epoch.current;
      setBusy(true);
      setError("");
      if (show) setSaved(false);
      setConfirmed(false);
      try {
        const result = await request({
          method: "GET",
          url: "/whatsapp/connections/" + connection.id + "/purposes",
        });
        if (token !== epoch.current) return;
        if (!validPurposes(result)) throw Error();
        setData(result);
        setSelected(result.purposes);
        setOpen(show);
      } catch (_) {
        if (show && token === epoch.current)
          setError(
            "Não foi possível consultar as finalidades. Tente novamente."
          );
      } finally {
        if (token === epoch.current) setBusy(false);
      }
    },
    [request, connection.id]
  );
  useEffect(() => {
    load(false);
    return () => {
      epoch.current += 1;
    };
  }, [owner, connection.id, load, refreshKey]);
  const replacements = data
    ? options.filter(
        ([p]) =>
          p !== "N8N" &&
          selected.includes(p) &&
          data.principals[p] &&
          data.principals[p] !== connection.id
      )
    : [];
  async function save(event) {
    event.preventDefault();
    if (busy || disabled || !data || (replacements.length && !confirmed))
      return;
    const token = ++epoch.current;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const result = await request({
        method: "POST",
        url: "/whatsapp/connections/" + connection.id + "/purposes",
        data: { purposes: selected, revision: data.revision },
      });
      if (token !== epoch.current) return;
      if (!validPurposes(result)) throw Error();
      setData(result);
      setSelected(result.purposes);
      setSaved(true);
      setConfirmed(false);
      setOpen(false);
      if (typeof onSaved === "function") onSaved();
    } catch (_) {
      if (token === epoch.current) {
        setData(null);
        setError(
          "Não foi possível salvar. Consulte novamente: outro administrador pode ter alterado as contas."
        );
      }
    } finally {
      if (token === epoch.current) setBusy(false);
    }
  }
  return (
    <div className="mt-4">
      {data && !open && (
        <p className="xv-settings-caption">
          {data.purposes.length
            ? "Usada para: " +
              options
                .filter(([p]) => data.purposes.includes(p))
                .map(([, label]) => label)
                .join(" · ")
            : "Nenhuma finalidade atribuída"}
        </p>
      )}
      <button
        type="button"
        className="btn btn-secondary"
        disabled={busy || disabled}
        onClick={open ? () => setOpen(false) : () => load()}
        aria-expanded={open}
      >
        {busy
          ? "Aguarde…"
          : open
          ? "Fechar finalidades"
          : "Definir finalidades"}
      </button>
      {error && (
        <p className="text-danger mt-3" role="alert">
          {error}
        </p>
      )}
      {saved && (
        <p className="text-success mt-3" role="status">
          Finalidades salvas. Envios continuam desligados.
        </p>
      )}
      {open && data && (
        <form
          onSubmit={save}
          className="mt-4"
          aria-label={"Finalidades de " + connection.label}
        >
          <fieldset disabled={busy || disabled}>
            <legend className="h6">Usar esta conta para</legend>
            <div className="kt-checkbox-list">
              {options.map(([key, label]) => (
                <label className="kt-checkbox d-block mb-3" key={key}>
                  <input
                    type="checkbox"
                    checked={selected.includes(key)}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setSelected((values) =>
                        checked
                          ? [...values, key]
                          : values.filter((p) => p !== key)
                      );
                      setConfirmed(false);
                      setSaved(false);
                    }}
                  />
                  {label}
                  <span />
                </label>
              ))}
            </div>
            <p className="xv-settings-caption">
              Documentos e Financeiro têm uma conta principal cada. O mesmo
              número pode atender ambos. n8n permite integração somente de
              consulta.
            </p>
            {replacements.length > 0 && (
              <div className="alert alert-warning d-block" role="status">
                <p>
                  Esta conta substituirá a principal atual de:{" "}
                  {replacements.map(([, label]) => label).join(", ")}. A conta
                  anterior deixará de atender essas finalidades.
                </p>
                <label className="kt-checkbox d-block">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  Confirmo a substituição
                  <span />
                </label>
              </div>
            )}
            <p className="xv-settings-caption">
              {connection.state === "CONNECTED"
                ? "Conta conectada. A atribuição não libera envios."
                : "Aguardando conexão. Nenhum envio será feito por esta conta enquanto estiver indisponível."}
            </p>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                busy || disabled || Boolean(replacements.length && !confirmed)
              }
            >
              Salvar finalidades
            </button>
          </fieldset>
        </form>
      )}
    </div>
  );
}
